import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ChatContextValue = {
  mobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedUserId: string | null;
  setSelectedUserId: React.Dispatch<React.SetStateAction<string | null>>;
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

type ChatProviderProps = {
  children: React.ReactNode;
};

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const toggleMobileSidebar = useCallback(() => {
    const finishToggle = () => setMobileSidebarOpen((prev) => !prev);

    if (typeof window === "undefined") {
      finishToggle();
      return;
    }

    const currentScroll = window.scrollY ?? window.pageYOffset ?? 0;
    if (currentScroll <= 0) {
      finishToggle();
      return;
    }

    let settled = false;
    const clear = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("scroll", handleScroll);
      finishToggle();
    };

    const handleScroll = () => {
      if ((window.scrollY ?? window.pageYOffset ?? 0) <= 0) {
        clear();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }

    window.setTimeout(clear, 600);
  }, [setMobileSidebarOpen]);

  const value = useMemo(
    () => ({
      mobileSidebarOpen,
      toggleMobileSidebar,
      setMobileSidebarOpen,
      selectedUserId,
      setSelectedUserId,
    }),
    [mobileSidebarOpen, toggleMobileSidebar, selectedUserId]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

